<template>
  <div class="pa-fee">
    <div class="pa-invoice">
      <div class="pa-invoice__head">
        <i class="material-icons pa-invoice__icon" aria-hidden="true">receipt_long</i>
        <div class="pa-invoice__heading">
          <h3>{{ packageInfo.label || 'POP Authority setup fee' }}</h3>
          <div v-if="headline" class="pa-invoice__sub">{{ headline }}</div>
        </div>
        <div class="pa-invoice__state">
          <span v-if="paid" class="pa-paid">
            <i class="material-icons" aria-hidden="true">check_circle</i>Paid
          </span>
          <span v-else class="pa-status pa-status--orange">Awaiting payment</span>
        </div>
      </div>

      <div class="pa-invoice__body">
        <div class="pa-invrow">
          <span>One-time setup fee</span>
          <b>${{ money(listPrice) }}</b>
        </div>
        <div v-if="paid && paidDiscount > 0" class="pa-invrow">
          <span>Coupon applied</span>
          <b class="pa-invrow__green">-${{ money(paidDiscount) }}</b>
        </div>
        <div v-else-if="appliedCoupon" class="pa-invrow">
          <span>
            Coupon {{ appliedCoupon.code }} ({{ couponLabel }})
            <button type="button" class="pa-link pa-invrow__remove" @click="removeCoupon">Remove</button>
          </span>
          <b class="pa-invrow__green">-${{ money(discount) }}</b>
        </div>
        <div v-if="paid" class="pa-invrow">
          <span><b>Total paid</b></span>
          <b>${{ money(paidTotal) }}</b>
        </div>
        <div v-else-if="appliedCoupon" class="pa-invrow">
          <span><b>Total due</b></span>
          <b>${{ money(finalPrice) }}</b>
        </div>

        <div class="pa-invoice__includes">Includes:</div>
        <div v-for="item in includes" :key="item" class="pa-invoice__include">
          <i class="material-icons" aria-hidden="true">check</i>{{ item }}
        </div>
        <div class="pa-invnote">
          The setup fee covers your audit. There is no separate audit purchase. Once
          {{ invoiceUrl && !paid ? 'this invoice' : 'this' }} is paid and you complete your set up we begin your
          content audit.
        </div>

        <template v-if="!paid && canEditOrder && !appliedCoupon">
          <div v-if="!showCoupon" class="pa-invoice__coupon-link">
            <button type="button" class="pa-link pa-invoice__coupon-toggle" @click="showCoupon = true">Have a coupon?</button>
          </div>
          <div v-else class="pa-invoice__coupon">
            <input
              class="pa-in pa-invoice__coupon-input"
              placeholder="Coupon code"
              v-model="couponCode"
              :disabled="couponLoading"
              @keydown.enter.prevent="applyCoupon"
            />
            <button
              type="button"
              class="pa-btn pa-btn--out"
              :disabled="!couponCode.trim() || couponLoading"
              @click="applyCoupon"
            >Apply coupon</button>
            <span v-if="couponError" class="pa-err pa-err--inline">{{ couponError }}</span>
          </div>
        </template>

        <div class="pa-invoice__actions">
          <template v-if="paid">
            <button type="button" class="pa-btn pa-btn--orange" @click="$emit('continue')">
              Next: tell us about your site
              <i class="material-icons" aria-hidden="true">arrow_forward</i>
            </button>
          </template>

          <template v-else-if="orderStatus === 'in_checkout'">
            <!-- an invoice sent by the campaign manager leaves the order in checkout: the client pays it
                 on Stripe's hosted invoice page -->
            <a
              v-if="invoiceUrl && !isAdmin"
              class="pa-btn pa-btn--orange"
              :href="invoiceUrl"
              target="_blank"
              rel="noopener noreferrer"
            >Pay invoice (${{ money(invoiceAmount) }})</a>
            <button
              type="button"
              class="pa-btn pa-btn--orange-out"
              :disabled="busy"
              @click="updateAudit({ set_user_editing: true })"
            >Cancel checkout session</button>
          </template>
          <template v-else-if="orderStatus === 'in_cart'">
            <button
              type="button"
              class="pa-btn pa-btn--orange-out"
              :disabled="busy"
              @click="updateAudit({ set_user_editing: true })"
            >Remove from cart</button>
          </template>
          <template v-else-if="orderStatus === 'in_cart_checkout'">
            <span class="pa-hint">In cart checkout session</span>
          </template>

          <template v-else>
            <template v-if="!isAdmin">
              <a
                v-if="invoiceUrl"
                class="pa-btn pa-btn--orange"
                :href="invoiceUrl"
                target="_blank"
                rel="noopener noreferrer"
              >Pay your invoice (${{ money(invoiceAmount) }})</a>
              <button
                v-else
                type="button"
                class="pa-btn pa-btn--orange"
                :disabled="busy"
                @click="buyNow('checkout')"
              >{{ finalPrice === 0 ? 'Activate setup (free)' : `Pay $${money(finalPrice)} now` }}</button>
            </template>

            <template v-else>
              <button
                type="button"
                class="pa-btn pa-btn--orange-out pa-btn--admin"
                :disabled="busy"
                @click="showSendInvoiceConfirm = true"
              >
                <i class="material-icons" aria-hidden="true">send</i>
                Send invoice (admin)
              </button>
              <button
                type="button"
                class="pa-btn pa-btn--out pa-btn--admin"
                :disabled="busy"
                @click="showSetPaidConfirm = true"
              >
                <i class="material-icons" aria-hidden="true">task_alt</i>
                Mark step complete (admin)
              </button>
            </template>
          </template>
        </div>

        <div v-if="invoiceUrl && isAdmin && !paid" class="pa-hint">
          Invoice already sent{{ sentOn ? ' on ' + sentOn : '' }}: the client pays from the emailed link.
          Sending again creates a new invoice.
        </div>
      </div>
    </div>

    <!-- Admin: send invoice confirm (design pa-app.jsx Modal, like the booster invoice: icon, muted body, price row) -->
    <div v-if="showSendInvoiceConfirm && isAdmin" class="pa-ovl" role="dialog" aria-modal="true" @click.self="showSendInvoiceConfirm = false">
      <div class="pa-cmodal">
        <div class="pa-cmodal__icon"><i class="material-icons" aria-hidden="true">receipt_long</i></div>
        <h3 class="pa-cmodal__title">Send the setup fee invoice?</h3>
        <p class="pa-cmodal__body">
          Emails an invoice to the site owner ({{ ownerEmail }}). The audit starts once it is paid and the site
          information is in.
        </p>
        <div class="pa-cmodal__rows">
          <div class="pa-cmodal__row"><span>One-time setup fee</span><b>${{ money(listPrice) }}</b></div>
          <div v-if="appliedCoupon" class="pa-cmodal__row">
            <span>Coupon {{ appliedCoupon.code }} ({{ couponLabel }})</span><b>-${{ money(discount) }}</b>
          </div>
          <div v-if="appliedCoupon" class="pa-cmodal__row"><span><b>Total due</b></span><b>${{ money(finalPrice) }}</b></div>
        </div>
        <div class="pa-cmodal__acts">
          <button type="button" class="pa-btn pa-btn--out" :disabled="busy" @click="showSendInvoiceConfirm = false">Cancel</button>
          <button type="button" class="pa-btn pa-btn--orange" :disabled="busy" @click="buyNow('invoice')">Send invoice</button>
        </div>
      </div>
    </div>

    <!-- Admin: mark as paid confirm (design DemoPanel "Mark invoice paid (admin)": price_check) -->
    <div v-if="showSetPaidConfirm && isAdmin" class="pa-ovl" role="dialog" aria-modal="true" @click.self="showSetPaidConfirm = false">
      <div class="pa-cmodal">
        <div class="pa-cmodal__icon"><i class="material-icons" aria-hidden="true">price_check</i></div>
        <h3 class="pa-cmodal__title">Mark the setup fee as paid?</h3>
        <p class="pa-cmodal__body">
          This marks the setup fee as paid ($0 recorded) and creates the Asana tasks, exactly like a real
          payment. The client moves on to the site information step.
        </p>
        <div class="pa-cmodal__acts">
          <button type="button" class="pa-btn pa-btn--out" :disabled="busy" @click="showSetPaidConfirm = false">Cancel</button>
          <button type="button" class="pa-btn pa-btn--orange" :disabled="busy" @click="setPaid">Mark as paid</button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { showPopAuthorityToast } from '@/utils/popAuthority'

const INCLUDES = [
  'Full website authority & content audit',
  'Data-driven keyword strategy',
  '6 month content silo roadmap',
  'Recorded strategy call with your dedicated SEO Manager',
]

function parseApiDate (value) {
  if (!value) return null
  const m = /^(\d{4})-(\d{2})-(\d{2})(?:[ T](\d{2}):(\d{2}):(\d{2}))?/.exec(String(value))
  if (!m) return null
  return new Date(Date.UTC(+m[1], +m[2] - 1, +m[3], +(m[4] || 0), +(m[5] || 0), +(m[6] || 0)))
}

/** "Sep. 18, 2026" (design date style: abbreviated month with a period). */
function formatDate (value) {
  const d = parseApiDate(value)
  if (!d) return ''
  return d
    .toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
    .replace(/^(\w{3}) /, '$1. ')
}

/**
 * Step 1 of the POP Authority setup wizard: the setup fee invoice card (design InvoiceStep).
 * Payment goes through the existing audit purchase pipeline (Stripe checkout, admin hosted invoice,
 * admin "set paid"); coupons must target the setup fee package.
 */
export default {
  name: 'PopAuthoritySetupFeeStep',
  props: {
    site: { type: Object, default: null },
    activeAudit: { type: Object, required: true },
    /** {key, label, price} from GET /api/white-glove-addon-packages/ */
    packageInfo: { type: Object, required: true },
    /** {sent_at, url, amount} of the unpaid hosted invoice (status engine), or null */
    setupInvoice: { type: Object, default: null },
    isAdmin: { type: Boolean, default: false },
  },
  data () {
    return {
      includes: INCLUDES,
      showCoupon: false,
      couponCode: '',
      couponLoading: false,
      couponError: '',
      appliedCoupon: null,
      busy: false,
      showSendInvoiceConfirm: false,
      showSetPaidConfirm: false,
    }
  },
  computed: {
    user () {
      return this.$store.state.user || null
    },
    selectedSubAccountRelation () {
      return this.$store.state.selectedSubAccountRelation
    },
    /** The billing email the invoice goes to (the owner's "Send invoices to" address, else its login email). */
    ownerEmail () {
      const r = this.selectedSubAccountRelation
      return r?.agencyInvoicingEmail || r?.agencyUserEmail || this.user?.invoicingEmail || this.user?.email || ''
    },
    orderStatus () {
      return this.activeAudit.order_status
    },
    paid () {
      return this.orderStatus === 'paid'
    },
    canEditOrder () {
      return this.orderStatus === 'user_editing'
    },
    listPrice () {
      return Number(this.packageInfo.price) || 0
    },
    discount () {
      if (!this.appliedCoupon) return 0
      const coupon = this.appliedCoupon.coupon
      let amountOff = Number(coupon.amountOff) || 0
      if (coupon.couponType === 'Percent Off') {
        amountOff = this.listPrice * amountOff / 100
      }
      return Math.min(this.listPrice, Math.max(0, amountOff))
    },
    finalPrice () {
      return Math.max(0, this.listPrice - this.discount)
    },
    couponLabel () {
      const coupon = this.appliedCoupon && this.appliedCoupon.coupon
      if (!coupon) return ''
      return coupon.couponType === 'Percent Off' ? `${coupon.amountOff}% off` : `$${coupon.amountOff} off`
    },
    paidTotal () {
      const v = Number(this.activeAudit.price_on_checkout)
      return Number.isFinite(v) ? v : this.listPrice
    },
    paidDiscount () {
      const before = Number(this.activeAudit.price_on_checkout_before_discount)
      const after = Number(this.activeAudit.price_on_checkout)
      if (!Number.isFinite(before) || !Number.isFinite(after)) return 0
      return Math.max(0, before - after)
    },
    invoiceUrl () {
      return this.activeAudit.stripe_hosted_invoice_url || (this.setupInvoice && this.setupInvoice.url) || ''
    },
    invoiceAmount () {
      const v = Number(this.activeAudit.stripe_hosted_invoice_amount)
      if (Number.isFinite(v) && v > 0) return v
      const fromStatus = Number(this.setupInvoice && this.setupInvoice.amount)
      return Number.isFinite(fromStatus) && fromStatus > 0 ? fromStatus : this.finalPrice
    },
    sentOn () {
      return formatDate(this.setupInvoice && this.setupInvoice.sent_at)
    },
    /** Stripe's customer-facing invoice number (design: "Invoice #A-1042, sent to ... on ..."). */
    invoiceNumber () {
      return this.activeAudit.stripe_hosted_invoice_number || (this.setupInvoice && this.setupInvoice.number) || ''
    },
    headline () {
      if (this.paid) {
        const on = formatDate(this.activeAudit.order_paid_at)
        return on ? `Paid on ${on}` : 'Paid'
      }
      if (this.invoiceUrl) {
        const lead = this.invoiceNumber ? `Invoice #${this.invoiceNumber}, sent` : 'Invoice sent'
        return `${lead} to ${this.ownerEmail}${this.sentOn ? ' on ' + this.sentOn : ''}`
      }
      return ''  // awaiting payment: the price line below says it all (tester, 2026-10-13)
    },
  },
  methods: {
    money (value) {
      const n = Number(value)
      return (Number.isFinite(n) ? n : 0).toFixed(2)
    },
    removeCoupon () {
      this.appliedCoupon = null
      this.couponCode = ''
      this.couponError = ''
    },
    applyCoupon () {
      const code = this.couponCode.trim()
      if (!code || this.couponLoading) return
      this.couponLoading = true
      this.couponError = ''
      return this.$store.dispatch('fetchValidatedCoupons', { promoCode: code, isRegisterPage: 0 })
        .then((response) => {
          const data = response && response.data
          if (data && data.status === 'SUCCESS') {
            const coupons = data.coupons || []
            const coupon = coupons.length === 1 ? coupons[0] : null
            if (
              coupon &&
              ['Percent Off', 'Amount Off'].includes(coupon.couponType) &&
              coupon.product === this.packageInfo.key
            ) {
              this.appliedCoupon = { code, coupon }
              showPopAuthorityToast(this, `Coupon ${code.toUpperCase()} applied: ${this.couponLabel} the setup fee`)
              return
            }
            this.couponError = 'That code is not valid for the setup fee.'
            return
          }
          this.couponError = (data && (data.errorMsg || data.msg)) || 'That code is not valid or has expired.'
        })
        .catch(() => {
          this.couponError = 'That code is not valid or has expired.'
        })
        .finally(() => {
          this.couponLoading = false
        })
    },
    buyNow (mode = 'checkout') {
      if (this.busy) return
      this.busy = true
      this.$store.commit('showLoading')
      return this.$store.dispatch('createStripePayment', {
        packageType: 'WHITE_GLOVE_ADDON_PURCHASE',
        userID: this.selectedSubAccountRelation?.ownerId || this.user.id,
        items: [{ type: 'audit_order', id: this.activeAudit.id }],
        redirect_front_url: this.$router.currentRoute.fullPath,
        redirect_front_for_cancel_url: this.$router.currentRoute.fullPath,
        promoCode: this.appliedCoupon ? this.appliedCoupon.code : undefined,
        mode,
      })
        .then((response) => {
          const data = response && response.data
          if (data && data.url) {
            window.open(data.url, '_self')
            return
          }
          if (mode === 'invoice' && data && data.status === 'SUCCESS') {
            this.showSendInvoiceConfirm = false
            // design pa-app.jsx sendInvoice("setup") toast: "Setup invoice #A-1042 sent to maria@romeonfoot.com"
            const number = data.invoiceNumber ? ` #${data.invoiceNumber}` : ''
            showPopAuthorityToast(this, `Setup invoice${number} sent to ${this.ownerEmail}`)
            this.$emit('audit-updated')
            return
          }
          if (data && data.status === 'FAILURE') {
            this.$notify({ group: 'info', type: 'error', text: data.msg || 'Could not start the payment' })
          }
        })
        .catch(() => {
          this.$notify({ group: 'info', type: 'error', text: 'Could not start the payment' })
        })
        .finally(() => {
          this.$store.commit('hideLoading')
          this.busy = false
        })
    },
    setPaid () {
      return this.updateAudit({ set_paid: 1 }).finally(() => {
        this.showSetPaidConfirm = false
      })
    },
    updateAudit (payload) {
      if (this.busy) return Promise.resolve()
      this.busy = true
      this.$store.commit('showLoading')
      return this.$store.dispatch('updateSiteContentAudit', {
        auditId: this.activeAudit.id,
        body: payload,
      })
        .then((response) => {
          const data = response && response.data
          if (data && data.status === 'SUCCESS') {
            if (payload.set_in_cart || payload.set_user_editing) {
              this.$store.commit('triggerRefreshSiloContentReportsOrdersCart', true)
            }
            this.$emit('audit-updated', data.audit)
          } else {
            this.$notify({ group: 'info', type: 'error', text: (data && data.msg) || 'Could not update the setup' })
          }
        })
        .catch(() => {
          this.$notify({ group: 'info', type: 'error', text: 'Could not update the setup' })
        })
        .finally(() => {
          this.$store.commit('hideLoading')
          this.busy = false
        })
    },
  },
}
</script>

<style lang="scss" scoped>
@import './setup.scss';
@import './designModal.scss';

.pa-invoice {
  border: 1px solid $pa-border-soft;
  border-radius: 12px;
  background: #fff;
  max-width: 720px;
  color: $pa-teal;

  &__head {
    display: flex;
    align-items: center;
    gap: 14px;
    padding: 18px 20px;
    border-bottom: 1px solid $pa-border-soft;
    flex-wrap: wrap;
  }

  &__icon {
    font-size: 30px;
    color: $pa-accent;
  }

  &__heading {
    flex: 1;
    min-width: 200px;

    h3 {
      margin: 0;
      font-size: 1.15rem;
      font-weight: 800;
    }
  }

  &__sub {
    color: $pa-muted;
    font-size: 0.88rem;
  }

  &__state {
    margin-left: auto;
  }

  &__body {
    padding: 18px 20px;
  }

  &__includes {
    font-size: 0.85rem;
    color: $pa-muted;
    padding: 6px 0 2px;
  }

  &__include {
    display: flex;
    gap: 8px;
    align-items: center;
    padding: 3px 0;
    font-size: 0.9rem;

    .material-icons {
      font-size: 16px;
      color: $pa-success;
    }
  }

  &__coupon-link {
    margin-top: 14px;
  }

  /* design pa-setup.jsx: "Have a coupon?" is plain teal text in the body size, not a blue link */
  &__coupon-toggle {
    color: $pa-teal;
    font-weight: 400;
    font-size: 0.9rem;

    &:hover {
      text-decoration: underline;
    }
  }

  &__coupon {
    display: flex;
    gap: 10px;
    margin-top: 14px;
    align-items: center;
    flex-wrap: wrap;
  }

  &__coupon-input {
    max-width: 220px;
    text-transform: uppercase;
  }

  &__actions {
    display: flex;
    gap: 10px;
    margin-top: 16px;
    flex-wrap: wrap;
    align-items: center;
  }
}

.pa-invrow {
  display: flex;
  justify-content: space-between;
  gap: 12px;
  padding: 9px 0;
  border-bottom: 1px dashed $pa-border-soft;
  font-size: 0.92rem;

  &:last-of-type {
    border-bottom: none;
  }

  b {
    font-weight: 800;
  }

  &__green {
    color: $pa-success;
  }

  &__remove {
    margin-left: 6px;
    font-size: 0.8rem;
  }
}

.pa-invnote {
  background: $pa-box-bg;
  border-radius: 8px;
  padding: 10px 14px;
  font-size: 0.85rem;
  color: $pa-muted;
  margin-top: 10px;
}

</style>
